/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ContentType } from 'client-code/action-details';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { ErrorTypes } from '../../core/error';
import { VodPlayActionErrorMapper } from '../../core/errormapper';
import { AxiosErrorAdapter } from '../../core/errormapper/adapters';
import { ServicesClientImpl } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { KeyManager } from '../../core/navigation/KeyManager';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import {
    TOverlayOptions,
    TVodPlayActionState,
    TVodPlayActionStateType,
    VodRequiredParams,
} from '../../core/types';
import { VodPlayAction } from '../../core/usecases/VodPlayAction';
import { IPlayable, IVodPlaybackModel } from '../../core/videoplayer/interfaces';
import { VodEntitlementType } from '../../views/common/VodPreview/VodPreviewProps';
import { showPCPinOverlayWithProgramRating } from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showVodFormatSelectorOverlay } from '../overlay/vodformatselectoroverlay/vodformatselectoroverlay.reducers';
import { showVodPlayErrorOverlay } from '../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import { showVodResumeOverlay } from '../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { showVodUpSellOverlay } from '../overlay/vodupselloverlay/vodupselloverlay.reducers';
import { hideOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { vodBrowseMainScreenSetNeedsRefreshAction } from '../vodbrowsemainscreen/vodbrowsemainscreen.actions';

/**
 * Helper function to dispatch error state with proper error mapping
 */
const dispatchVodPlayError = (errorType: string, error?: any) => (dispatch: Function) => {
    const vodPlayError = new VodPlayActionErrorMapper(new AxiosErrorAdapter()).getErrorByCode(
        errorType,
        error,
    );
    dispatch(
        notifyVodPlayActionState({
            type: TVodPlayActionStateType.ERROR,
            error: vodPlayError,
        }),
    );
};

/**
 * Handle preview playback logic
 */
const handlePreviewPlayback = (actionDetailsList: any[]) => (dispatch: Function) => {
    if (actionDetailsList.length > 0) {
        PlaybackDataModelImpl.getInstance().setActionDetails(actionDetailsList[0]);
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.PERFORM_PC_CHECK,
                selectedAction: actionDetailsList[0],
            }),
        );
    } else {
        dispatch(dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNAVAILABLE_V433));
    }
};

/**
 * Handle normal VOD playback flow based on available actions
 */
const handleNormalPlayback = (actionDetailsList: any[]) => (dispatch: Function) => {
    const actionCount = actionDetailsList.length;

    if (actionCount === 0) {
        dispatch(dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNAVAILABLE_V433));
    } else if (actionCount === 1) {
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.OFFER_SELECTED,
                selectedAction: actionDetailsList[0],
            }),
        );
    } else {
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.SHOW_FORMAT_SELECTOR,
            }),
        );
    }
};

/**
 * Initialize VOD playback process
 * Fetches action details and routes to appropriate playback flow
 */
const initVodPlaybackProcess =
    (vodRequiredParams: VodRequiredParams) => async (dispatch: Function) => {
        try {
            // Start ignoring keys at the beginning of VOD playback process
            if (!KeyManager.getInstance().isKeyIgnoring()) {
                KeyManager.getInstance().startKeyIgnoring();
            }

            // Set up playback data model with required parameters
            PlaybackDataModelImpl.getInstance().setVodRequiredParams(vodRequiredParams);

            // Fetch VOD action details from service
            const actionDetails = await new VodPlayAction().getVodOttActionDetails(
                vodRequiredParams,
            );
            PlaybackDataModelImpl.getInstance().setActionDetailsList(actionDetails);

            const actionDetailsList = actionDetails.actionDetails.watchActionsList;

            // Route to appropriate playback flow
            if (vodRequiredParams.isPreview) {
                dispatch(handlePreviewPlayback(actionDetailsList));
            } else {
                dispatch(handleNormalPlayback(actionDetailsList));
            }
        } catch (error) {
            // Handle any errors during initialization
            dispatch(dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_CANNOT_INITIATE_PROCESS_V431));
        }
    };

const processSelectedOfferWithEntitlementCheck = (selectedAction) => (dispatch: Function) => {
    // Check entitlement directly from the action kernel
    const { entitlement } = selectedAction.kernel || {};
    const entitlementType = (entitlement as VodEntitlementType) || VodEntitlementType.Unknown;

    PlaybackDataModelImpl.getInstance().setActionDetails(selectedAction);

    switch (entitlementType) {
        case VodEntitlementType.Unknown: {
            dispatch(
                dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNKNOWN_ENTITLEMENT_V432),
            );
            break;
        }

        case VodEntitlementType.Rentable:
        case VodEntitlementType.Purchasable: {
            dispatch(dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_RENTALS_NOT_ALLOWED_V233));
            break;
        }

        case VodEntitlementType.Subscribable: {
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_UPSELL,
                    selectedAction,
                }),
            );
            break;
        }

        default: {
            // Entitled content - proceed with PC check
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.PERFORM_PC_CHECK,
                    selectedAction,
                }),
            );
            break;
        }
    }
};

const prepareVODResumeOverlay = (selectedAction) => (dispatch: Function) => {
    const vodPlayAction = new VodPlayAction();
    const hasPausePoint = vodPlayAction.checkForPausePoint(selectedAction);
    const isPreview = PlaybackDataModelImpl.getInstance().getNewVodRequiredParams()?.isPreview;
    if (hasPausePoint && !isPreview) {
        // Show VOD resume/restart overlay for content with pause point and not Preview (trailer)
        dispatch(showVodResumeOverlay());
    } else {
        // No pause point or Preview (trailer) playback - proceed directly to playback preparation
        dispatch(prepareForPlayback(selectedAction, true));
    }
};

const prepareVodUpSellOverlay = (selectedAction) => (dispatch: Function) => {
    // Get title from selectedAction kernel
    const title =
        selectedAction?.kernel?.vodName || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);

    // Get subtitle from action details following formatting rules
    const actionDetailsList = PlaybackDataModelImpl.getInstance().getActionDetailsList();
    const actionDetails = actionDetailsList?.actionDetails;

    let subtitle = actionDetails?.title || '';

    if (actionDetails) {
        const { contentType, movieYear, episodeNumber, seasonNumber, episodeTitle } = actionDetails;

        switch (contentType) {
            case ContentType.Movie:
                if (movieYear) {
                    subtitle += ` (${movieYear})`;
                }
                break;

            case ContentType.Episode: {
                // Check if we have any season/episode info to show
                const hasSeasonInfo = seasonNumber && seasonNumber > 0;
                const hasEpisodeInfo = episodeNumber && episodeNumber > 0;

                if (hasSeasonInfo || hasEpisodeInfo) {
                    const seasonEpisodeText = AppUtils.generateSeasonEpisodeText(
                        hasSeasonInfo ? seasonNumber : 0,
                        hasEpisodeInfo ? episodeNumber : 0,
                    );
                    subtitle += ` - ${seasonEpisodeText}`;
                }

                if (episodeTitle) {
                    subtitle += ` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
                } else {
                    subtitle += ` ${LEFT_QUOTE}${Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)}${RIGHT_QUOTE}`;
                }
                break;
            }

            default:
                // For any other content type, just use the title
                break;
        }
    }

    dispatch(showVodUpSellOverlay(title, subtitle));
};

const prepareForPlayback = (selectedAction, isStartOver) => async (dispatch: Function) => {
    // Check if VOD purchase is needed based on entitlement
    const { entitlement } = selectedAction.kernel || {};
    const entitlementType = (entitlement as VodEntitlementType) || VodEntitlementType.Unknown;

    if (entitlementType === VodEntitlementType.FreeZeroPurchasable) {
        // Need to purchase the VOD item first
        const vodPlayAction = new VodPlayAction();
        if (!KeyManager.getInstance().isKeyIgnoring()) {
            KeyManager.getInstance().startKeyIgnoring();
        }
        try {
            await vodPlayAction.purchaseVodItem(selectedAction);
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.START_VOD_PLAYBACK,
                    selectedAction,
                    isStartOver,
                }),
            );
        } catch (error) {
            dispatch(dispatchVodPlayError(ErrorTypes.VOD_PLAY_ACTION_PURCHASE_FAILED_V361, error));
        }
    } else {
        // No purchase needed - start VOD playback directly
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.START_VOD_PLAYBACK,
                selectedAction,
                isStartOver,
            }),
        );
    }
};

const startVodPlayback = (selectedAction, isStartOver: boolean) => (dispatch: Function) => {
    const vodRequiredParams = PlaybackDataModelImpl.getInstance().getNewVodRequiredParams();

    const vodPlaybackModel: IVodPlaybackModel = {
        id: selectedAction.kernel.offerId,
        collapsedVodItemId:
            vodRequiredParams.itemType === 'unifiedCollection' ||
            vodRequiredParams.itemType === 'content'
                ? vodRequiredParams?.collapsedVodItemId
                : undefined,
        contentSupplierPartnerId: vodRequiredParams?.contentSupplierPartnerId,
        isStartOver,
        videoProviderId: selectedAction.kernel?.partnerId,
        isPreview: vodRequiredParams?.isPreview || false,
    };

    const playable: IPlayable = {
        playbackContentType: PlaybackContentType.VOD,
        vodPlaybackModel,
    };

    PlaybackDataModelImpl.getInstance().setPlaybackData(PlaybackContentType.VOD);
    dispatch(hideOverlayDialog());

    dispatch(vodBrowseMainScreenSetNeedsRefreshAction(true));

    // Start VOD playback
    dispatch(startVideoPlayback(playable));
    // Stop ignoring keys when playback is started or after playback is finished
    if (KeyManager.getInstance().isKeyIgnoring()) {
        KeyManager.getInstance().stopKeyIgnoring();
    }
};

const processKeyOkOnFormatSelector = () => (dispatch: Function, getState: Function) => {
    const { overlayHighlight, overlayDialog } = getState();
    const { verticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;

    const selectedOption: TOverlayOptions = options[verticalPosition];
    const { action } = selectedOption.value;

    dispatch(hideOverlayDialog());
    dispatch(
        notifyVodPlayActionState({
            type: TVodPlayActionStateType.OFFER_SELECTED,
            selectedAction: action,
        }),
    );
};

const processMouseClickVodFormatSelectorOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === VodPlayAction.GLOBAL_MOUSE_TRAP_FORMAT_SELECTOR_OVERLAY) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(processKeyOkOnFormatSelector());
    };

const processKeyOkOnVodResumeOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayHighlight } = getState();
    const { verticalPosition } = overlayHighlight;

    dispatch(hideOverlayDialog());

    const selectedAction = PlaybackDataModelImpl.getInstance().getNewActionDetails();

    // Handle resume vs start over based on selection
    if (verticalPosition === 0) {
        // Resume playing selected
        dispatch(prepareForPlayback(selectedAction, false));
    } else {
        // Start over from beginning selected
        dispatch(prepareForPlayback(selectedAction, true));
    }
};

const processMouseClickVodResumeOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === VodPlayAction.GLOBAL_MOUSE_TRAP_VOD_RESUME_OVERLAY) {
        dispatch(hideOverlayDialog());
        return;
    }

    dispatch(processKeyOkOnVodResumeOverlay());
};

/**
 * Checks for parental control violations for VOD content.
 * Based on the violation, a new state should be returned.
 * @param contentRatingInfo - Information about the content's rating.
 */
// eslint-disable-next-line no-unused-vars
const checkVodPCViolation = (action: any) => (dispatch: Function) => {
    const { internalRatingList, isAdult, contentType } = action.kernel;

    const services = new ServicesClientImpl();
    const isPCViolated = services.checkPCViolationsWithRatings(
        internalRatingList || [],
        isAdult || false,
        contentType,
    );
    const restrictedRatingName = services.getRestrictedRatingName(internalRatingList || []);
    if (isPCViolated) {
        dispatch(
            showPCPinOverlayWithProgramRating(
                () => {
                    dispatch(
                        notifyVodPlayActionState({
                            type: TVodPlayActionStateType.PC_CHECK_SUCCESS,
                            selectedAction: action,
                        }),
                    );
                },
                isAdult || false,
                restrictedRatingName,
            ),
        );
    } else {
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.PC_CHECK_SUCCESS,
                selectedAction: action,
            }),
        );
    }
};

/**
 * Function to notify the Vod Play Action state.
 * This function is dispatched to handle the Vod Play Action state.
 * All actions related to Vod Play Action should be dispatched through this function.
 *
 * @param {TVodPlayActionState} state - The current state of the Vod Play Action.
 */
// eslint-disable-next-line no-unused-vars
export const notifyVodPlayActionState = (state: TVodPlayActionState) => (dispatch: Function) => {
    switch (state.type) {
        case TVodPlayActionStateType.INIT:
            dispatch(initVodPlaybackProcess(state.vodRequiredParams));
            break;
        case TVodPlayActionStateType.START_VOD_PLAYBACK:
            dispatch(startVodPlayback(state.selectedAction, state.isStartOver));
            break;
        case TVodPlayActionStateType.OFFER_SELECTED:
            dispatch(processSelectedOfferWithEntitlementCheck(state.selectedAction));
            break;
        case TVodPlayActionStateType.VOD_UPSELL:
            dispatch(prepareVodUpSellOverlay(state.selectedAction));
            break;
        case TVodPlayActionStateType.PURCHASE_ERROR:
            dispatch(showVodPlayErrorOverlay(state.error));
            break;
        case TVodPlayActionStateType.SHOW_FORMAT_SELECTOR:
            dispatch(showVodFormatSelectorOverlay());
            break;
        case TVodPlayActionStateType.ERROR:
            dispatch(showVodPlayErrorOverlay(state.error));
            break;
        case TVodPlayActionStateType.PERFORM_PC_CHECK:
            dispatch(checkVodPCViolation(state.selectedAction));
            break;
        case TVodPlayActionStateType.PC_CHECK_SUCCESS:
            dispatch(prepareVODResumeOverlay(state.selectedAction));
            break;
        case TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK:
            dispatch(processKeyOkOnFormatSelector());
            break;
        case TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK:
            dispatch(processMouseClickVodFormatSelectorOverlay());
            break;
        case TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK:
            dispatch(processKeyOkOnVodResumeOverlay());
            break;
        case TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK:
            dispatch(processMouseClickVodResumeOverlay());
            break;
        default:
            throw new Error('Unknown state type');
    }
};
